/**
 * Scanbrix user lifecycle — project-based orders, not SaaS plans.
 * Activation = download → (lead | not_a_lead) → workspace → scan → order.
 */
import { isIphoneProModel, isIpadProModel } from "./appleDeviceModelNames";

export const LIFECYCLE_STAGES = [
  {
    id: "downloaded",
    label: "Downloaded",
    color: "#64748b",
    bg: "rgba(100,116,139,0.14)",
    description: "Installed the app / profile created",
    activation: true,
  },
  {
    id: "lead",
    label: "Lead",
    color: "#3b82f6",
    bg: "rgba(59,130,246,0.12)",
    description: "LiDAR-capable device — can scan in-app",
    activation: true,
  },
  {
    id: "not_a_lead",
    label: "Not a lead",
    color: "#94a3b8",
    bg: "rgba(148,163,184,0.14)",
    description: "Non-LiDAR device — unlikely to complete an in-app scan",
    activation: false,
  },
  {
    id: "sample_downloaded",
    label: "Sample files",
    color: "#a855f7",
    bg: "rgba(168,85,247,0.12)",
    description: "Downloaded sample deliverables (marketing link)",
    activation: true,
  },
  {
    id: "workspace_created",
    label: "Workspace created",
    color: "#0ea5e9",
    bg: "rgba(14,165,233,0.12)",
    description: "Created a workspace",
    activation: true,
  },
  {
    id: "has_scanned",
    label: "Has scanned",
    color: "#8b5cf6",
    bg: "rgba(139,92,246,0.12)",
    description: "Started an in-app scan session",
    activation: true,
  },
  {
    id: "uploaded_scans",
    label: "Uploaded scans",
    color: "#7c3aed",
    bg: "rgba(124,58,237,0.12)",
    description: "Uploaded scan data to Scanbrix (Firestore scans/files)",
    activation: true,
  },
  {
    id: "trial_order",
    label: "Trial order",
    color: "#f59e0b",
    bg: "rgba(245,158,11,0.14)",
    description: "Placed a test job using a promo code",
    activation: false,
  },
  {
    id: "paid_user",
    label: "Paid user",
    color: "#10b981",
    bg: "rgba(16,185,129,0.12)",
    description: "Paid for a project order (no promo / full price)",
    activation: false,
  },
];

export const STAGE_MAP = Object.fromEntries(LIFECYCLE_STAGES.map((s) => [s.id, s]));

/** Monotonic rank for auto-advance (higher = further in funnel). */
export const STAGE_RANK = {
  downloaded: 10,
  not_a_lead: 20,
  lead: 20,
  sample_downloaded: 25,
  workspace_created: 30,
  has_scanned: 40,
  uploaded_scans: 45,
  trial_order: 50,
  paid_user: 60,
};

export const LEGACY_STAGE_MAP = {
  demo_scheduled: "lead",
  free_scan_delivered: "uploaded_scans",
  paid_customer: "paid_user",
  churned: "downloaded",
};

export const ACTIVATION_STAGE_IDS = new Set(
  LIFECYCLE_STAGES.filter((s) => s.activation).map((s) => s.id)
);

export function normalizeLifecycleStage(stage) {
  const s = String(stage || "").trim();
  if (!s) return "downloaded";
  if (STAGE_MAP[s]) return s;
  return LEGACY_STAGE_MAP[s] || s;
}

export function stageRank(stage) {
  const n = normalizeLifecycleStage(stage);
  return STAGE_RANK[n] ?? 0;
}

export function shouldAdvanceStage(currentStage, targetStage) {
  const current = normalizeLifecycleStage(currentStage);
  const target = normalizeLifecycleStage(targetStage);
  if (current === target) return false;
  if (target === "lead" && current === "not_a_lead") return false;
  if (target === "not_a_lead" && current === "lead") return false;
  return stageRank(target) > stageRank(current);
}

export function contactDeviceModelRaw(contact) {
  if (!contact) return "";
  return (
    contact.device_model ??
    contact.deviceModel ??
    contact.apple_device_model ??
    contact.appleDeviceModel ??
    contact.device_hardware_id ??
    contact.deviceHardwareId ??
    ""
  );
}

export function contactHasLidarDevice(contact) {
  const raw = contactDeviceModelRaw(contact);
  if (!raw) return null;
  return isIphoneProModel(raw) || isIpadProModel(raw);
}

export function lidarLeadStageForContact(contact) {
  const lidar = contactHasLidarDevice(contact);
  if (lidar === null) return null;
  return lidar ? "lead" : "not_a_lead";
}

const PROMO_ORDER_FIELDS = [
  "promo_code",
  "promoCode",
  "coupon",
  "coupon_code",
  "discount_code",
  "promotion_code",
  "voucher_code",
  "referral_code",
  "applied_promo",
  "appliedPromo",
  "stripe_promotion_code",
  "stripePromotionCode",
];

const PROMO_NESTED_OBJECTS = ["metadata", "stripe", "checkout", "payment"];

function collectPromoCodeValues(order) {
  const values = [];
  if (!order || typeof order !== "object") return values;
  for (const f of PROMO_ORDER_FIELDS) {
    const v = order[f];
    if (v != null && String(v).trim()) values.push(String(v).trim());
  }
  for (const key of PROMO_NESTED_OBJECTS) {
    const obj = order[key];
    if (!obj || typeof obj !== "object") continue;
    for (const f of PROMO_ORDER_FIELDS) {
      const v = obj[f];
      if (v != null && String(v).trim()) values.push(String(v).trim());
    }
  }
  return values;
}

export function orderUsesPromoCode(order, configuredPromoCode = "") {
  if (!order || typeof order !== "object") return false;
  if (order.is_trial === true || order.trial === true || order.is_promo === true) return true;
  if (order.has_promo === true || order.promo_applied === true || order.coupon_applied === true) return true;
  const values = collectPromoCodeValues(order);
  if (values.length > 0) return true;
  const cfg = configuredPromoCode != null ? String(configuredPromoCode).trim().toLowerCase() : "";
  if (cfg) {
    return values.some((v) => v.toLowerCase() === cfg);
  }
  return false;
}

export function orderPaidStage(order, configuredPromoCode = "") {
  if (!order || typeof order !== "object") return null;
  if (orderUsesPromoCode(order, configuredPromoCode)) return "trial_order";
  return "paid_user";
}

export function isSampleDownloadEvent(eventName, metadata = {}) {
  const n = String(eventName || "").toLowerCase();
  if (n === "sample_download" || n === "sample_downloaded" || n === "sample_files_downloaded") return true;
  if (n.includes("sample") && (n.includes("download") || n.includes("file"))) return true;
  const meta = metadata && typeof metadata === "object" ? metadata : {};
  if (meta.sample === true || meta.is_sample === true || meta.content_type === "sample") return true;
  const slug = String(meta.slug || meta.link_slug || "").toLowerCase();
  if (slug.includes("sample")) return true;
  return false;
}

export function activationProgressPct(stage) {
  const r = stageRank(stage);
  const max = STAGE_RANK.uploaded_scans;
  if (max <= 0) return 0;
  return Math.min(100, Math.round((Math.min(r, max) / max) * 100));
}

export function isPreOrderActivationStage(stage) {
  const n = normalizeLifecycleStage(stage);
  return ACTIVATION_STAGE_IDS.has(n) && stageRank(n) < STAGE_RANK.trial_order;
}
